import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import OstroDynamicContent from 'app/components/OstroDynamicContent';
import { useTailorIsOpen } from 'app/hooks/useTailorIsOpen';
import useHandleSearchResultClick from 'app/modules/tailor/hooks/useHandleSearchResultClick';
import useCustomization from 'app/hooks/useCustomization';
import CloseButton from 'app/components/Header/CloseButton';
import { motion } from 'framer-motion';
import useSuggestedArticleTracking, {
  getSuggestedTopicType,
} from 'app/modules/tailor/hooks/useSuggestedArticleTracking';
import { useAnalytics } from 'app/context/Analytics';
import { PosthogEventName } from 'app/copied/constants/PosthogEventName';
import { rootContainer } from 'app/shadowRoot';
import useIsMobile from 'app/hooks/useIsMobile';
import { SuggestedEntry } from 'app/copied/models/ChatModules';
import { useSuggestedArticle } from 'app/context/SuggestedArticleProvider';
import useNBA from 'app/hooks/useNBA';

import { NBRLabel } from './NBRLabel';

let expandedState = true;
const easeOpen = [0.4, 0, 0.2, 1];
const easeClose = [0.6, 0.05, 0.8, 0.2];

const NextBestResource = ({ article }: { article: SuggestedEntry }) => {
  const { t } = useTranslation();
  const { setIsOpen } = useTailorIsOpen();
  const customization = useCustomization();
  const nba = useNBA();
  const isMobile = useIsMobile();
  const [expanded, setExpanded] = useState(expandedState);

  const { setLastViewedArticle } = useSuggestedArticle();
  const handleSearchResultClick = useHandleSearchResultClick();
  const { captureSuggestedTopicClicked } = useSuggestedArticleTracking();
  const { captureEventWithActiveScreen } = useAnalytics();

  const nbrButtonAriaLabel = article?.title
    ? t('aria_labels.open_article_button', { title: article?.title })
    : t('aria_labels.open_widget_button');
  const prependText = 'Next';
  const autoCollapse = nba?.auto_collapse_nba || false;
  const showDelay = (nba?.delay_before_show || 0) * 1000;
  const collapseDelay = (nba?.delay_before_collapse || 0) * 1000;

  const actionIconSize = {
    width: customization?.open_button_action_icon_width || 52,
    height: customization?.open_button_action_icon_height || 52,
  };
  const buttonIconSize = {
    width: 14,
    height: 14,
  };

  const collapseButton = useCallback(() => {
    expandedState = false;
    setExpanded(false);
  }, []);

  const handleNBRClick = () => {
    if (!article) return;
    //Check if link to Virtual Assistant node
    const id = article.node_ref_id || article.id;
    if (id) {
      article.id = id;
    }

    if (article.description && !article.external_link && !article.resource_link) {
      setIsOpen(true);
    }

    setLastViewedArticle(article);

    captureSuggestedTopicClicked(article, null, 'nonWidget', 0, expanded, 'may25');

    setTimeout(() => {
      handleSearchResultClick({
        result: article,
        resultIndex: 0,
        isSuggested: false,
        linkDetails: { source: 'nonWidget' },
      });
    }, 100);
  };

  const handleCloseClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    collapseButton();
  };

  const handleWidgetButtonClick = () => {
    const text = rootContainer.querySelector('#ostro-webchat-button-text')?.textContent;

    captureEventWithActiveScreen(PosthogEventName.WidgetClicked, {
      nudgeOn: false,
      bounceOn: false,
      nudgeTime: 0,
      ctaCopy: prependText,
      iconStyle: 'tailor',
      nbaCTA: true,
      secondaryCTAstring: text,
      suggestedTopicType: article ? getSuggestedTopicType(article) : 'Content',
      suggestedTopicTitle: article?.title,
      suggestedTopicSource: 'nonWidget',
    });
    setIsOpen(true);
  };

  // show NBR after timeout -----------------
  const [shown, setShown] = useState(!isMobile || !expanded);
  useEffect(() => {
    if (shown) return;

    const showButton = () => {
      setShown(true);
    };

    const id = window.setTimeout(showButton, showDelay);
    return () => clearTimeout(id);
  }, [shown, setShown, showDelay]);
  // ---------------------------------------

  // collapse NBR after scroll -----------------
  useEffect(() => {
    if (!isMobile || !autoCollapse || !shown) return;

    const handleScroll = () => {
      window.setTimeout(collapseButton, collapseDelay);
    };

    window.addEventListener('scroll', handleScroll);

    return () => window.removeEventListener('scroll', handleScroll);
  }, [isMobile, shown, autoCollapse, collapseButton, collapseDelay]);
  // ---------------------------------------
  if (!shown) return null;

  return (
    <div
      style={{ maxWidth: isMobile ? '320px' : '372px' }}
      className="widget-position-mobile md:widget-position-desktop flex flex-col gap-2 cursor-pointer pointer-events-auto"
    >
      <motion.div
        className="relative"
        layout
        initial={{ opacity: 0, transform: 'translateX(100%)' }}
        animate={{ opacity: 1, transform: 'translateX(0)' }}
        transition={{ duration: 1, ease: expanded ? easeClose : easeOpen }}
      >
        {expanded && (
          <CloseButton
            id="ostro-webchat-close-text-button"
            onClick={handleCloseClick}
            circleClassName="bg-white w-6 h-6 shadow-md"
            className="absolute left-[-14px] top-[-12px] w-7 h-7 z-10"
            useCustomIcon
          />
        )}
        <button
          id="nbr"
          style={{ padding: expanded ? 24 : 16 }}
          className="relative p-6 rounded-[14px] bg-brand-nbaButtonBackground self-end w-full text-start"
          onClick={handleNBRClick}
          aria-label={nbrButtonAriaLabel}
        >
          <div
            style={{ gap: expanded ? 28 : 8 }}
            className="flex items-center [&_p]:mb-0"
          >
            {expanded ? (
              <div
                id="ostro-webchat-bubble-text"
                className="flex grow flex-col"
              >
                <OstroDynamicContent className="whitespace-normal grow text-md leading-normal line-clamp-2 md:line-clamp-3">
                  <NBRLabel text={prependText} />
                  <span
                    id="ostro-suggested-article-title"
                    className="text-sm font-semibold [&_*]:(inline) text-brand-nbaFontColor"
                    dangerouslySetInnerHTML={{ __html: article?.title || '' }}
                  />
                  <br />
                  {article?.description && (
                    <span
                      id="ostro-suggested-article-description"
                      style={{ marginTop: isMobile ? 8 : 12 }}
                      className="inline-block [&_*]:(inline) text-brand-nbaFontColor"
                      dangerouslySetInnerHTML={{ __html: article?.description || '' }}
                    />
                  )}
                </OstroDynamicContent>
              </div>
            ) : (
              <NBRLabel text={prependText} />
            )}
            <motion.img
              layoutId="nbr-icon"
              className={`w-[${actionIconSize.width}px] h-[${actionIconSize.height}px] cursor-pointer`}
              src={customization?.open_button_action_icon}
              alt=""
              aria-hidden="true"
            />
          </div>
        </button>
      </motion.div>

      <motion.button
        id="widget"
        layout
        initial={{ opacity: 0, transform: 'translateX(100%)' }}
        animate={{ opacity: 1, transform: 'translateX(0)' }}
        transition={{ duration: 1, delay: 0.2, ease: expanded ? easeClose : easeOpen }}
        style={{ borderRadius: 14, borderWidth: 1 }}
        className="flex gap-1 bg-brand-openBubbleBackground items-center px-3 py-2 cursor-pointer self-end border-buttonWidth border-buttonColor box-content h-[18px] overflow-hidden"
        onClick={handleWidgetButtonClick}
        aria-label={t('aria_labels.open_widget_button')}
      >
        {nba?.open_nba_button_icon && (
          <motion.img
            layoutId="ostro-widget-icon"
            className={`w-[${buttonIconSize.width}px] h-[${buttonIconSize.height}px]`}
            src={nba?.open_nba_button_icon}
            alt=""
            aria-hidden="true"
          />
        )}
        {expanded && (
          <div
            id="ostro-widget-text"
            className="flex grow flex-col gap-3"
          >
            <OstroDynamicContent
              id="ostro-webchat-button-text"
              className="whitespace-normal grow [&_p]:mb-0"
              content={customization?.open_button_text}
            />
          </div>
        )}
      </motion.button>
    </div>
  );
};

export default NextBestResource;
